import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const empty = { name: "", role: "", quote: "", rating: 5, avatar_url: "", published: true };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = () => api.get("/admin/testimonials").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setErr("");
    try {
      const payload = { ...editing };
      delete payload._new; delete payload.id; delete payload.created_at;
      payload.rating = Number(payload.rating);
      if (editing._new) await api.post("/admin/testimonials", payload);
      else await api.put(`/admin/testimonials/${editing.id}`, payload);
      load(); setEditing(null);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Yorumu silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="section-eyebrow">Yönetim</span>
          <h1 className="font-display text-4xl mt-2">Öğrenci Yorumları</h1>
        </div>
        <button onClick={() => { setEditing({ ...empty, _new: true }); setErr(""); }} className="btn-primary text-sm">
          <Plus className="w-4 h-4" /> Yeni Yorum
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {items.map(t => (
          <div key={t.id} className="bg-surface border border-white/10 p-6">
            <div className="flex items-center gap-3">
              {t.avatar_url && <img src={t.avatar_url} alt={t.name} className="w-12 h-12 rounded-full object-cover border border-gold-30" />}
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg">{t.name}</div>
                <div className="text-xs text-white/50">{t.role}</div>
              </div>
              <div className="flex">{[...Array(t.rating || 5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-gold text-gold" />)}</div>
            </div>
            <div className="hairline my-4" />
            <p className="text-sm text-white/70 italic">"{t.quote}"</p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => { setEditing({ ...t, _new: false }); setErr(""); }} className="text-white/60 hover:text-gold p-2"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(t.id)} className="text-white/60 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-white/50 py-16">Henüz yorum yok.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-start justify-center p-4 md:p-10 overflow-y-auto">
          <div className="w-full max-w-2xl bg-surface border border-gold-30 p-8 my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl">{editing._new ? "Yeni Yorum" : "Yorumu Düzenle"}</h2>
              <button onClick={() => setEditing(null)} className="text-white/60 hover:text-gold"><X className="w-6 h-6" /></button>
            </div>
            {err && <div className="bg-red-950/40 border border-red-800 text-red-200 p-3 text-sm mb-4">{err}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Ad Soyad"><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="admin-input" /></Field>
              <Field label="Rol / Şirket"><input value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="admin-input" /></Field>
              <Field label="Avatar URL" span2><input value={editing.avatar_url} onChange={(e) => setEditing({ ...editing, avatar_url: e.target.value })} className="admin-input" /></Field>
              <Field label="Puan (1-5)"><input type="number" min={1} max={5} value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: e.target.value })} className="admin-input" /></Field>
              <label className="flex items-center gap-2 text-sm text-white/80 mt-6"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Yayında</label>
              <Field label="Yorum" span2><textarea rows={4} value={editing.quote} onChange={(e) => setEditing({ ...editing, quote: e.target.value })} className="admin-input" /></Field>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-outline text-sm">İptal</button>
              <button onClick={save} className="btn-primary text-sm"><Save className="w-4 h-4" /> Kaydet</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.admin-input { width: 100%; background: #0F0F14; border: 1px solid rgba(255,255,255,0.08); padding: 0.6rem 0.9rem; color: white; font-family: 'Outfit', sans-serif; outline: none; font-size: 0.875rem; } .admin-input:focus { border-color: rgba(212,175,55,0.6); }`}</style>
    </div>
  );
}

function Field({ label, span2, children }) {
  return <label className={`block ${span2 ? "md:col-span-2" : ""}`}><span className="text-xs uppercase tracking-widest text-white/60 mb-2 inline-block">{label}</span>{children}</label>;
}
